import { trimSilence } from './utils';
import { BufferSource } from './BufferSource';
import { FrameBufferHistory } from './FrameBufferHistory';

/**
 * AudioManager handles Web Audio API integration
 * Responsible for:
 * - AudioContext lifecycle management
 * - MediaStream management
 * - AnalyserNode configuration
 * - Audio data retrieval
 * - BufferSource support for static buffer visualization
 */
export class AudioManager {
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private mediaStream: MediaStream | null = null;
  private audioBufferSource: AudioBufferSourceNode | null = null;
  private bufferSource: BufferSource | null = null;
  private dataArray: Float32Array | null = null;
  private frequencyData: Uint8Array | null = null;
  private frameBufferHistory: FrameBufferHistory = new FrameBufferHistory();

  /**
   * Initialize analyser node and data arrays
   */
  private initializeAnalyser(): void {
    if (!this.audioContext) {
      throw new Error('AudioContext must be initialized before creating analyser');
    }

    // Create analyser node with high resolution
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 4096; // Higher resolution for better waveform
    this.analyser.smoothingTimeConstant = 0; // No smoothing for accurate waveform
    
    // Create data array for time domain data
    const bufferLength = this.analyser.fftSize;
    this.dataArray = new Float32Array(bufferLength);
    
    // Create frequency data array for FFT
    this.frequencyData = new Uint8Array(this.analyser.frequencyBinCount);
  }

  /**
   * Start audio capture and analysis
   */
  async start(): Promise<void> {
    try {
      // Request microphone access
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Set up Web Audio API
      this.audioContext = new AudioContext();
      const source = this.audioContext.createMediaStreamSource(this.mediaStream);
      
      // Initialize analyser and data arrays
      this.initializeAnalyser();
      
      // Connect nodes
      source.connect(this.analyser!);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      throw error;
    }
  }

  /**
   * Start audio playback from file
   */
  async startFromFile(file: File): Promise<void> {
    try {
      // Read file as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      
      // Close existing AudioContext if present to avoid resource leak
      if (this.audioContext && this.audioContext.state !== 'closed') {
        await this.audioContext.close();
      }

      // Set up Web Audio API
      this.audioContext = new AudioContext();
      
      // Decode audio data
      let audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      
      // Trim silence from the beginning and end to avoid gaps in loop playback
      audioBuffer = trimSilence(audioBuffer);
      
      // Initialize analyser and data arrays
      this.initializeAnalyser();
      
      // Create buffer source for looping playback
      this.audioBufferSource = this.audioContext.createBufferSource();
      this.audioBufferSource.buffer = audioBuffer;
      this.audioBufferSource.loop = true;
      
      // Connect nodes: source -> analyser -> destination
      this.audioBufferSource.connect(this.analyser!);
      this.analyser!.connect(this.audioContext.destination);
      
      // Start playback
      this.audioBufferSource.start(0);
    } catch (error) {
      console.error('Error loading audio file:', error);
      throw error;
    }
  }

  /**
   * Start visualization from a static buffer without audio playback
   * Uses the same Web Audio API architecture as startFromFile for consistent performance
   * @param bufferSource - BufferSource instance containing audio data
   */
  async startFromBuffer(bufferSource: BufferSource): Promise<void> {
    try {
      // Close existing AudioContext if present to avoid resource leak
      if (this.audioContext && this.audioContext.state !== 'closed') {
        await this.audioContext.close();
      }

      // Set up Web Audio API (same as startFromFile)
      this.audioContext = new AudioContext();
      
      // Get the audio data from BufferSource
      // Ensure clean state before reading all data
      bufferSource.reset();
      const chunkSize = 4096;
      bufferSource.setChunkSize(chunkSize);
      
      // Create an AudioBuffer from the Float32Array data
      const sampleRate = bufferSource.getSampleRate();
      const dataLength = bufferSource.getLength();
      const audioBuffer = this.audioContext.createBuffer(1, dataLength, sampleRate);
      
      // Copy data to AudioBuffer by reading all chunks
      const channelData = audioBuffer.getChannelData(0);
      let position = 0;
      while (position < dataLength) {
        const chunk = bufferSource.getNextChunk();
        if (!chunk) break;
        const remaining = dataLength - position;
        if (chunk.length <= remaining) {
          channelData.set(chunk, position);
          position += chunk.length;
        } else {
          channelData.set(chunk.subarray(0, remaining), position);
          position += remaining;
        }
      }
      
      // Initialize analyser and data arrays (same as startFromFile)
      this.initializeAnalyser();
      
      // Create buffer source for looping (same as startFromFile)
      this.audioBufferSource = this.audioContext.createBufferSource();
      this.audioBufferSource.buffer = audioBuffer;
      this.audioBufferSource.loop = true;
      
      // Connect nodes: source -> analyser (no connection to destination = no audio playback)
      // This gives us the same AnalyserNode FFT as startFromFile
      this.audioBufferSource.connect(this.analyser!);
      
      // Start processing (no audio output since not connected to destination)
      this.audioBufferSource.start(0);
    } catch (error) {
      console.error('Error starting from buffer:', error);
      throw error;
    }
  }

  /**
   * Stop audio capture and clean up resources
   */
  async stop(): Promise<void> {
    if (this.audioBufferSource) {
      try {
        this.audioBufferSource.stop();
      } catch (error) {
        // Ignore error if already stopped or in invalid state
      }
      try {
        this.audioBufferSource.disconnect();
      } catch (error) {
        // Ignore disconnect errors
      }
      this.audioBufferSource = null;
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
    if (this.audioContext) {
      try {
        await this.audioContext.close();
      } catch (error) {
        console.error('Error closing AudioContext:', error);
      }
      this.audioContext = null;
    }
    this.analyser = null;
    this.dataArray = null;
    this.frequencyData = null;
    this.frameBufferHistory.clear();
  }

  /**
   * Get time-domain data (waveform)
   * Also updates the frame buffer history for extended FFT
   */
  getTimeDomainData(): Float32Array | null {
    // Both file and buffer modes now use analyser node
    if (!this.analyser || !this.dataArray) {
      return null;
    }
    // @ts-ignore - Web Audio API type definitions issue
    this.analyser.getFloatTimeDomainData(this.dataArray);
    
    // Store a copy of the current frame buffer for history
    this.frameBufferHistory.updateHistory(this.dataArray);
    
    return this.dataArray;
  }
  
  /**
   * Get extended time-domain data by concatenating past frame buffers
   * @param multiplier - Buffer size multiplier (1, 4, or 16)
   * @returns Combined buffer or null if insufficient history
   */
  getExtendedTimeDomainData(multiplier: 1 | 4 | 16): Float32Array | null {
    return this.frameBufferHistory.getExtendedBuffer(multiplier, this.dataArray);
  }

  /**
   * Get frequency-domain data (FFT)
   * Both file and buffer modes now use AnalyserNode
   */
  getFrequencyData(): Uint8Array | null {
    // Both file and buffer modes now use analyser node
    if (!this.analyser || !this.frequencyData) {
      return null;
    }
    // @ts-ignore - Web Audio API type definitions issue
    this.analyser.getByteFrequencyData(this.frequencyData);
    return this.frequencyData;
  }

  /**
   * Get sample rate
   */
  getSampleRate(): number {
    return this.audioContext?.sampleRate || 0;
  }

  /**
   * Get FFT size
   */
  getFFTSize(): number {
    if (this.bufferSource) {
      return 4096; // Fixed FFT size for buffer mode
    }
    return this.analyser?.fftSize || 0;
  }

  /**
   * Get frequency bin count
   */
  getFrequencyBinCount(): number {
    if (this.bufferSource) {
      return 2048; // Half of FFT size
    }
    return this.analyser?.frequencyBinCount || 0;
  }

  /**
   * Check if audio system is ready
   */
  isReady(): boolean {
    // Buffer mode: check if buffer source and data array are ready
    if (this.bufferSource) {
      return this.dataArray !== null;
    }
    // Normal mode: check if audio context and analyser are ready
    return this.audioContext !== null && this.analyser !== null;
  }
}
